/*
  product-countdown.js — Per-product timers for metafield custom.timer
  -------------------------------------------------------------------
  - Finds all elements with [data-countdown][data-end]
  - data-end can be UNIX seconds (preferred) or an ISO8601 string
  - Updates every second with D:H:M:S
  - Handles Theme Editor section reloads
*/
(function ProductCountdowns(){
  'use strict';

  const SELECTOR = '[data-countdown][data-end]';
  const ACTIVE_ATTR = 'data-countdown-active';

  let ticking = false;
  let rafId = null;

  // Cache all timers
  const timers = new Set();

  function pad2(n){ return n < 10 ? '0' + n : '' + n; }

  function parseEnd(value){
    if (value == null) return null;
    // Prefer UNIX seconds
    if (/^\d+$/.test(String(value))) {
      const sec = parseInt(value, 10);
      if (!Number.isNaN(sec)) return sec * 1000; // ms
    }
    // Fallback: parse ISO8601
    const t = Date.parse(String(value));
    return Number.isFinite(t) ? t : null;
  }

  function bindAll(root){
    const nodes = (root || document).querySelectorAll(SELECTOR);
    nodes.forEach((el)=>{
      if (el.hasAttribute(ACTIVE_ATTR)) return; // already bound

      const endRaw = el.getAttribute('data-end');
      const endMs = parseEnd(endRaw);
      if (!endMs) return; // nothing to do

      const obj = {
        el,
        endMs,
        dd: el.querySelector('[data-dd]'),
        hh: el.querySelector('[data-hh]'),
        mm: el.querySelector('[data-mm]'),
        ss: el.querySelector('[data-ss]'),
        expiredEl: el.querySelector('.cdt__expired')
      };
      el.setAttribute(ACTIVE_ATTR, '');
      timers.add(obj);
    });
    startTicking();
  }

  function startTicking(){
    if (ticking) return;
    ticking = true;
    tick();
  }

  function stopTicking(){
    ticking = false;
    if (rafId) cancelAnimationFrame(rafId);
  }

  function diffParts(ms){
    const total = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return {days, hours, minutes, seconds};
  }

  function updateOne(t){
    const now = Date.now();
    const remain = t.endMs - now;
    if (remain <= 0){
      // Expired
      if (t.dd) t.dd.textContent = '00';
      if (t.hh) t.hh.textContent = '00';
      if (t.mm) t.mm.textContent = '00';
      if (t.ss) t.ss.textContent = '00';
      if (t.expiredEl) t.expiredEl.hidden = false;
      t.el.classList.add('cdt--expired');
      timers.delete(t);
      return;
    }

    const {days, hours, minutes, seconds} = diffParts(remain);
    if (t.dd) t.dd.textContent = pad2(days);
    if (t.hh) t.hh.textContent = pad2(hours);
    if (t.mm) t.mm.textContent = pad2(minutes);
    if (t.ss) t.ss.textContent = pad2(seconds);
    
    // Add urgency classes based on time remaining
    const totalSeconds = Math.floor(remain / 1000);
    t.el.classList.remove('cdt--urgent', 'cdt--critical');
    
    if (totalSeconds <= 300) { // 5 minutes
      t.el.classList.add('cdt--critical');
    } else if (totalSeconds <= 3600) { // 1 hour
      t.el.classList.add('cdt--urgent');
    }
  }

  function tick(){
    // Align to second boundaries to avoid drift
    const now = Date.now();
    timers.forEach(updateOne);

    if (timers.size === 0){
      stopTicking();
      return;
    }

    const delay = 1000 - (now % 1000);
    rafId = window.setTimeout(()=>{
      if (ticking) tick();
    }, delay);
  }

  // Public API (optional)
  window.ShopThisDropCountdowns = {
    refresh(root){ bindAll(root || document); },
    count(){ return timers.size; }
  };

  // Initial bind
  document.addEventListener('DOMContentLoaded', ()=> bindAll(document));

  // Theme Editor support
  document.addEventListener('shopify:section:load', (evt)=> bindAll(evt.target));
  document.addEventListener('shopify:section:unload', (evt)=>{
    // Remove timers inside unloaded section
    const els = evt.target.querySelectorAll(SELECTOR);
    els.forEach((el)=>{
      if (el.hasAttribute(ACTIVE_ATTR)){
        // find object
        for (const t of timers){
          if (t.el === el){ timers.delete(t); break; }
        }
        el.removeAttribute(ACTIVE_ATTR);
      }
    });
  });
})();
